import type { SeatInfo } from "@app/protocol";
import { useT } from "../i18n/context";
import { gameOf } from "../variants";
import { type AnyView, isTeamGame, type TeamView } from "../variants/types";
import { wordsOf } from "../variants/words";
import { Avatar } from "./Avatar";

const PILL =
  "flex items-center rounded-pill border-[1.5px] border-edge bg-panel shadow-float backdrop-blur-[10px]";

function Divider() {
  return <span aria-hidden="true" className="h-[18px] w-px bg-ink/20" />;
}

/** "3rd hand | To 51 | trump". */
function Progress({ view }: { view: AnyView }) {
  const { t } = useT();
  const label = view.goal === null ? t("table.bar.handsLabel") : t("table.bar.targetLabel");
  const { BarState } = gameOf(view);
  return (
    <div className={`${PILL} gap-1 px-1.5 py-1`}>
      <span className="px-3 py-1 text-[13px] font-bold">
        {t("table.bar.hand", { hand: view.hand })}
      </span>
      <Divider />
      <span className="px-3 text-[13px] text-ink/75">
        {label} <b className="tabular-nums text-ink">{view.goal ?? view.length}</b>
      </span>
      <Divider />
      <BarState view={view} />
    </div>
  );
}

function SeatScores({
  view,
  seats,
  yourSeat,
}: {
  view: AnyView;
  seats: readonly (SeatInfo | null)[];
  yourSeat: number | null;
}) {
  return (
    <div className={`${PILL} gap-1.5 px-1.5 py-1`}>
      {view.scores.map((score, seat) => (
        <span
          key={seats[seat]?.userId ?? seat}
          title={seats[seat]?.name ?? "?"}
          className="flex items-center gap-1.5 rounded-pill bg-ink/8 py-[3px] pl-[3px] pr-2.5"
        >
          <Avatar member={seats[seat] ?? null} size={24} />
          <span
            className={`text-[13px] font-bold tabular-nums ${seat === yourSeat ? "text-gold" : ""}`}
          >
            {score}
          </span>
        </span>
      ))}
    </div>
  );
}

/** Partnership games: us first, with Spades' bag count. */
function TeamScores({ view, yourSeat }: { view: TeamView; yourSeat: number | null }) {
  const { t } = useT();
  const note = wordsOf(view).teamNote;
  const us = (yourSeat ?? 0) % 2;
  const rows = [
    { label: t("spades.team.us"), team: us, mine: true },
    { label: t("spades.team.them"), team: 1 - us, mine: false },
  ];
  return (
    <div className={`${PILL} gap-1.5 px-1.5 py-1`}>
      {rows.map((row) => (
        <span
          key={row.label}
          className="flex items-baseline gap-1.5 rounded-pill bg-ink/8 px-3 py-1"
        >
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink/70">
            {row.label}
          </span>
          <span
            className={`text-[14px] font-extrabold tabular-nums ${row.mine ? "text-gold" : ""}`}
          >
            {view.teamScores[row.team] ?? 0}
          </span>
          {note === undefined ? null : (
            <span className="text-[10px] text-ink/55">{note(view, row.team, t)}</span>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * The middle of the top bar at the table: the scores first, then the hand and the target with the
 * trump (owner, 20 Sept 2026: the score belongs on top, in every game). The eye goes to the board
 * first and to which hand it is second.
 */
export function TableInfo({
  view,
  seats,
  yourSeat,
}: {
  view: AnyView;
  seats: readonly (SeatInfo | null)[];
  yourSeat: number | null;
}) {
  return (
    <>
      {isTeamGame(view) ? (
        <TeamScores view={view} yourSeat={yourSeat} />
      ) : (
        <SeatScores view={view} seats={seats} yourSeat={yourSeat} />
      )}
      <Progress view={view} />
    </>
  );
}
